import React, { useState } from 'react';
import cn from 'classnames';

import { ProgramRuleEntity, RequirementEntity } from 'Models/Entities';

import {
	Button, Display, QuickTypes, Sizes,
} from 'Views/Components/Button/Button';
// eslint-disable-next-line max-len
import OrConditionLabel from 'Views/Pages/Frontend/StudyPlanWizard/OrConditionLabel';

import { DataIndex } from 'Util/DataIndexUtils';
import { IGeneratedPlanFeedback } from 'Queries/Hooks/StudyPlan/types';
import InfoModal from 'Views/Components/Modal/InfoModal';
import classnames from 'classnames';
import { useWizardContext } from 'Views/Components/Wizard/WizardContext';
import { IStudyPlanWizardData } from 'Views/Pages/Frontend/StudyPlanWizard/StudyPlanWizard';
import {
	RequirementNode,
	checkRequirementRulesViolation,
} from 'Views/Pages/Frontend/StudyPlanWizard/RequirementTreeGeneration';
import { CourseSelectionItemProps }
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/CourseSelectionItem';

import ChildRequirementList
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/ChildRequirementList';

import CourseSelectionList
	from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelection/RequirementContainer/CourseSelectionList';
import { useRequirementIndex } from 'Views/Pages/Frontend/StudyPlanWizard/RequirementIndexContext';
import { Conditional } from 'Views/Components/Conditional/Conditional';
import CourseAccordion from 'Views/Components/CourseAccordion';
import CourseSelectionNotice from 'Views/Pages/Frontend/StudyPlanWizard/CourseSelectionNotice';
import InfoIconButton from 'Views/Components/InfoIconButton/InfoIconButton';

interface IRequirementContainerProps {
	requirementSubtree: RequirementNode;
	depth?: number;
	requisiteWarningsByCourseId: DataIndex<IGeneratedPlanFeedback['requisiteWarnings']>
	updatePlanSelections: CourseSelectionItemProps['updatePlanSelections']
}

const RequirementContainer = (props: IRequirementContainerProps) => {
	const {
		requirementSubtree,
		depth = 0,
		requisiteWarningsByCourseId,
		updatePlanSelections,
	} = props;

	const topLevel = depth === 0;

	const { data } = useWizardContext<IStudyPlanWizardData>();
	const {
		planSelections,
		courseOfferingsById,
		completedCourseIds,
		duplicateCourseIds,
	} = useRequirementIndex();

	const [
		showingRequirementDetails, setShowingRequirementDetails,
	] = useState<(RequirementEntity | ProgramRuleEntity) | null>(null);
	const [collapsed, setCollapsed] = useState(false);

	const isRequirementContainer = requirementSubtree.requirement.category === 'REQUIREMENTS';
	const isCourseContainer = requirementSubtree.requirement.category === 'COURSES';

	/**
	 * If the requirement has an OR condition and is a
	 * requirements container, we always display a requirement
	 * condition label. This element should then be subject
	 * to the slight bottom overlap of child requirements
	 */
	const shouldDisplayRequirementCondition = (
		isRequirementContainer
		&& requirementSubtree.requirement.condition === 'OR'
	);

	const overlapItem = shouldDisplayRequirementCondition
		? 'label'
		: 'header';

	const showRequirementInfoModal = (requirement: RequirementEntity | ProgramRuleEntity) => {
		setShowingRequirementDetails(requirement);
	};

	const closeRequirementInfoModal = () => {
		setShowingRequirementDetails(null);
	};

	const hasViolatedRequirementRules = checkRequirementRulesViolation(requirementSubtree);

	const showRequirementDescription = requirementSubtree.requirement.description?.length > 0
		|| hasViolatedRequirementRules;

	return (
		<div
			className={
				cn(
					'course-selection-group',
					{
						collapsed,
						'top-level': topLevel,
						'requirement-container': isRequirementContainer,
					},
				)
			}
		>
			<div
				className={cn(
					'group-header',
					`depth-${depth}`,
					{ 'overlap-item': overlapItem === 'header' },
				)}
			>
				<span className="group-header__title">
					{requirementSubtree.requirement.displayName}
				</span>
				<span className="group-header__right">
					{
						showRequirementDescription
							&& (
								<InfoIconButton
									ariaLabel={`show ${requirementSubtree.requirement.displayName} requirement rules`}
									hasError={hasViolatedRequirementRules}
									onClick={() => showRequirementInfoModal(requirementSubtree.requirement)}
								/>
							)
					}
					{
						requirementSubtree.unitMinimum !== undefined
							&& (
								<span className="unit-count">
									{requirementSubtree.unitCount}/{requirementSubtree.unitMinimum}
								</span>
							)
					}
					<Button
						className={cn(
							'expand-collapse-btn icon-only',
							{
								'icon-remove': !collapsed,
								'icon-keyboard-arrow-down': collapsed,
							},
						)}
						quickTypes={QuickTypes.Secondary}
						display={Display.Text}
						sizes={Sizes.Small}
						onClick={() => setCollapsed(prev => !prev)}
						buttonProps={{
							'aria-label': `${collapsed
								? 'expand'
								: 'collapse'} ${requirementSubtree.requirement.displayName} accordion`,
						}}
					/>
				</span>
			</div>
			<OrConditionLabel
				className={cn({ 'overlap-item': overlapItem === 'label' })}
				childRequirements={requirementSubtree.childNodes.map(node => node.requirement)}
				visible={shouldDisplayRequirementCondition}
			/>
			<Conditional
				condition={hasViolatedRequirementRules}
				component={CourseAccordion.Label}
				componentProps={{
					className: 'course-selection-notice condition-label overlap-item',
					content: (
						<CourseSelectionNotice />
					),
					isError: true,
				}}
			/>
			{
				isRequirementContainer
					&& (
						<ChildRequirementList
							childNodes={requirementSubtree.childNodes}
							newDepth={depth + 1}
							requisiteWarningsByCourseId={requisiteWarningsByCourseId}
							updatePlanSelections={updatePlanSelections}
						/>
					)
			}
			{
				(
					isCourseContainer
					&& data?.studyPlan?.campusId !== undefined
				)
					&& (
						<CourseSelectionList
							requirement={requirementSubtree.requirement}
							offeringIdsByCourseId={requirementSubtree.offeringIdsByCourseId}
							courseOfferingsById={courseOfferingsById}
							planSelections={planSelections}
							completedCourses={completedCourseIds}
							duplicateCourses={duplicateCourseIds}
							requisiteWarningsByCourseId={requisiteWarningsByCourseId}
							updatePlanSelections={updatePlanSelections}
							preferredCampusId={data.studyPlan.campusId}
						/>
					)
			}
			{ showingRequirementDetails ? (
				<InfoModal
					isOpen
					title={showingRequirementDetails.displayName}
					description={showingRequirementDetails.description}
					onCloseModal={closeRequirementInfoModal}
				/>
			) : null}
		</div>
	);
};

export default RequirementContainer;
